import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiHelpCircle } from 'react-icons/fi';
import {
  plans,
  faqs,
  formatINR,
  annualPerMonth,
  annualPrice,
  moduleCount,
  type Plan,
} from '../data/product';
import { Reveal, SectionHeading } from '../components/ui';

export type BillingCycle = 'monthly' | 'annual';

/** The monthly / annual switch. Lifted out so the home teaser can show it too. */
export const BillingToggle: React.FC<{
  cycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
  invert?: boolean;
}> = ({ cycle, onChange, invert = false }) => (
  <div className="flex flex-col items-center gap-3">
    <div
      className={`relative inline-flex items-center rounded-full p-1 ${
        invert ? 'bg-white/10 border border-white/15' : 'bg-slate-100 border border-slate-200'
      }`}
      role="tablist"
      aria-label="Billing cycle"
    >
      {(['monthly', 'annual'] as BillingCycle[]).map((c) => {
        const active = cycle === c;
        return (
          <button
            key={c}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(c)}
            className={`relative z-10 rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors ${
              active
                ? invert
                  ? 'text-ink-900'
                  : 'text-white'
                : invert
                ? 'text-slate-300 hover:text-white'
                : 'text-slate-500 hover:text-ink-700'
            }`}
          >
            {active && (
              <motion.span
                layoutId={invert ? 'cycle-pill-invert' : 'cycle-pill'}
                transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                className={`absolute inset-0 -z-10 rounded-full ${
                  invert ? 'bg-white' : 'bg-gradient-to-r from-primary-600 to-primary-500'
                }`}
              />
            )}
            {c}
          </button>
        );
      })}
    </div>
    <span className={`text-xs font-medium ${invert ? 'text-accent-300' : 'text-emerald-600'}`}>
      Save 2 months with annual billing
    </span>
  </div>
);

const PriceBlock: React.FC<{ plan: Plan; cycle: BillingCycle }> = ({ plan, cycle }) => {
  const perMonth = cycle === 'annual' ? annualPerMonth(plan) : plan.monthly;
  return (
    <div>
      <div className="flex items-end gap-1">
        <span className="font-display text-4xl font-bold tracking-tight text-ink-800">
          ₹{formatINR(perMonth)}
        </span>
        <span className="mb-1.5 text-sm text-slate-500">/mo</span>
      </div>
      <p className="mt-1 h-5 text-xs text-slate-500">
        {cycle === 'annual'
          ? `₹${formatINR(annualPrice(plan))} billed yearly + GST`
          : 'billed monthly + GST'}
      </p>
    </div>
  );
};

/**
 * The three tier cards. Shared between the pricing page and the home teaser,
 * so pricing shown on the landing page can never contradict the pricing page.
 * There is no payment gateway — every CTA routes to Contact, which is a
 * deliberate product decision, not a stub.
 */
export const PricingCards: React.FC<{ cycle: BillingCycle }> = ({ cycle }) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
    {plans.map((plan, i) => {
      const featured = plan.popular;
      return (
        <Reveal key={plan.id} delay={i * 0.08}>
          <div
            className={`relative flex h-full flex-col rounded-3xl p-7 transition-all duration-300 ${
              featured
                ? 'border-2 border-primary-500 bg-white shadow-card-hover lg:-mt-4 lg:mb-4'
                : 'border border-slate-200 bg-white shadow-card hover:-translate-y-1 hover:shadow-card-hover'
            }`}
          >
            {featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary-600 to-accent-500 px-4 py-1 text-xs font-semibold text-white shadow-glow">
                Most popular
              </span>
            )}

            <h3 className="font-display text-xl font-bold text-ink-800">{plan.name}</h3>
            <p className="mt-1.5 min-h-[2.5rem] text-sm text-slate-500">{plan.tagline}</p>

            <div className="my-5">
              <PriceBlock plan={plan} cycle={cycle} />
            </div>

            <div className="mb-5 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-ink-700">
              {plan.seats}
            </div>

            <Link
              to="/contact"
              className={featured ? 'btn-primary w-full' : 'btn-secondary w-full'}
            >
              {plan.cta}
              <FiArrowRight className="h-4 w-4" />
            </Link>

            {plan.inherits && (
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {plan.inherits}
              </p>
            )}
            <ul className="mt-3 space-y-3">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <span
                    className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full ${
                      featured ? 'bg-primary-100 text-primary-600' : 'bg-emerald-100 text-emerald-600'
                    }`}
                  >
                    <FiCheck className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      );
    })}
  </div>
);

const FaqItem: React.FC<{ q: string; a: string; defaultOpen?: boolean }> = ({
  q,
  a,
  defaultOpen = false,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-semibold text-ink-700">{q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-50 text-primary-600"
        >
          <span className="text-lg leading-none">+</span>
        </motion.span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600">{a}</p>
      </motion.div>
    </div>
  );
};

export const Pricing: React.FC = () => {
  const [cycle, setCycle] = React.useState<BillingCycle>('monthly');

  return (
    <div className="overflow-hidden">
      {/* Header */}
      <section className="relative bg-ink-900 pb-24 pt-32 sm:pt-40">
        <div className="absolute inset-0 blueprint-grid mask-fade opacity-70" />
        <div
          className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-primary-600/20 blur-3xl"
          aria-hidden
        />
        <div className="container-custom relative">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <span className="pill-invert mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                Simple, honest pricing
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl">
                One price. <span className="text-gradient-animate">All {moduleCount} modules.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mx-auto mt-5 max-w-2xl lede-invert">
                No per-seat surprises, no per-module add-ons. Pick the plan that fits the size of
                your firm today and move up whenever you are ready.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-9 flex justify-center">
                <BillingToggle cycle={cycle} onChange={setCycle} invert />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Cards - pulled up to overlap the dark header */}
      <section className="relative -mt-16 pb-8">
        <div className="container-custom relative">
          <PricingCards cycle={cycle} />
          <Reveal>
            <p className="mt-8 text-center text-sm text-slate-500">
              All plans include SSL security, isolated per-firm data, responsive mobile access and
              PDF &amp; Excel export. Prices exclude GST.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Questions"
            title="Everything you might be wondering"
            sub="If your question isn't here, our team is one message away."
          />
          <div className="mx-auto mt-12 grid max-w-3xl gap-3">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.04}>
                <FaqItem q={f.q} a={f.a} defaultOpen={i === 0} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section pt-0">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-700 via-primary-600 to-accent-600 px-6 py-14 text-center shadow-glow sm:px-12 sm:py-16">
            <div className="absolute inset-0 blueprint-grid-fine opacity-40" aria-hidden />
            <div className="relative">
              <FiHelpCircle className="mx-auto mb-4 h-8 w-8 text-white/80" />
              <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
                Not sure which plan fits?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-primary-50">
                Tell us about your firm and we'll set up a live workspace with your own data — walk
                through every module before you decide anything.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 shadow-lg transition-transform hover:scale-[1.03] sm:text-base"
                >
                  Book a free demo
                  <FiArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/about" className="btn-ghost-invert">
                  See how it works
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
